import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AREAS, EXP_STATUS, fmtDate, PRIORITY, RISK } from "../lib/labels";
import { useCompanies, useTemplates, userName, useUsers } from "../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";

function NewExpedienteDialog({ open, onClose, onCreated }) {
  const templates = useTemplates();
  const { companies } = useCompanies();
  const { user } = useAuth();
  const [form, setForm] = useState({ title: "", description: "", area: "Legal", template_id: "", priority: "media", risk: "medio", target_date: "", company_id: "" });

  useEffect(() => { if (templates.length && !form.template_id) setForm((f) => ({ ...f, template_id: templates[0].id })); }, [templates]);

  const submit = async () => {
    try {
      const payload = { ...form, target_date: form.target_date ? new Date(form.target_date).toISOString() : undefined, company_id: form.company_id || undefined };
      const r = await api.post("/expedientes", payload);
      toast.success(`Expediente ${r.data.code} creado en Borrador`);
      onClose();
      onCreated?.(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-expediente-dialog">
        <DialogHeader><DialogTitle>Nuevo expediente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Título *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-exp-title" /></div>
          <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-exp-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Área</Label>
              <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                <SelectTrigger data-testid="new-exp-area"><SelectValue /></SelectTrigger>
                <SelectContent>{AREAS.filter((a) => a !== "General").map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proceso (plantilla)</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger data-testid="new-exp-template"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="new-exp-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Riesgo</Label>
              <Select value={form.risk} onValueChange={(v) => setForm({ ...form, risk: v })}>
                <SelectTrigger data-testid="new-exp-risk"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(RISK).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha objetivo</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="new-exp-target" /></div>
            <div>
              <Label>Empresa relacionada</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="new-exp-company"><SelectValue placeholder="Ninguna" /></SelectTrigger>
                <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-slate-500">Se creará en estado <strong>Borrador</strong>. El proceso se activa con la acción «Iniciar» desde el detalle.</p>
          <Button onClick={submit} disabled={!form.title.trim()} className="w-full" data-testid="new-exp-submit">Crear expediente</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Expedientes() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { map } = useUsers();
  const [newOpen, setNewOpen] = useState(params.get("nuevo") === "1");
  const filters = {
    area: params.get("area") || "", estado: params.get("estado") || "",
    riesgo: params.get("riesgo") || "", q: params.get("q") || "", ia: params.get("ia") || "",
  };
  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["expedientes", filters],
    queryFn: async () => (await api.get("/expedientes", {
      params: { area: filters.area || undefined, status: filters.estado || undefined, risk: filters.riesgo || undefined, q: filters.q || undefined, ia: filters.ia || undefined },
    })).data,
  });

  return (
    <div data-testid="expedientes-page">
      <PageHeader
        title="Expedientes"
        subtitle={filters.ia === "pendiente" ? "Expedientes con análisis IA pendientes de validación humana" : "Asuntos empresariales con seguimiento procesado. Los estados solo cambian mediante acciones controladas."}
        actions={<Button onClick={() => setNewOpen(true)} data-testid="new-expediente-button"><Plus className="mr-1 h-4 w-4" /> Nuevo expediente</Button>}
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por título, código o etiqueta…" value={filters.q} onChange={(e) => setFilter("q", e.target.value)} className="h-8 w-64" data-testid="exp-search-input" />
        <Select value={filters.area} onValueChange={(v) => setFilter("area", v === "todas" ? "" : v)}>
          <SelectTrigger className="h-8 w-44" data-testid="exp-area-filter"><SelectValue placeholder="Área" /></SelectTrigger>
          <SelectContent><SelectItem value="todas">Todas las áreas</SelectItem>{AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.estado} onValueChange={(v) => setFilter("estado", v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-52" data-testid="exp-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos los estados</SelectItem>{Object.entries(EXP_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.riesgo} onValueChange={(v) => setFilter("riesgo", v === "todos" ? "" : v)}>
          <SelectTrigger className="h-8 w-36" data-testid="exp-risk-filter"><SelectValue placeholder="Riesgo" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos</SelectItem>{Object.entries(RISK).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando expedientes…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin expedientes" description="No hay expedientes con estos filtros." testid="expedientes-empty" />
      ) : (
        <div className="ts-card overflow-hidden">
          <Table data-testid="expedientes-table">
            <TableHeader>
              <TableRow>
                <TableHead>Expediente</TableHead>
                <TableHead className="hidden md:table-cell">Área</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="hidden sm:table-cell">Riesgo</TableHead>
                <TableHead className="hidden lg:table-cell">Propietario</TableHead>
                <TableHead className="hidden w-40 xl:table-cell">Avance</TableHead>
                <TableHead className="hidden lg:table-cell">Objetivo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id} className="ts-table-row" onClick={() => navigate(`/expedientes/${e.id}`, { state: { crumb: e.code } })} data-testid={`expediente-row-${e.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{e.title}</p>
                    <p className="font-mono text-xs text-slate-400">{e.code}{e.current_step ? ` · ${e.current_step}` : ""}</p>
                  </TableCell>
                  <TableCell className="hidden text-sm text-slate-600 md:table-cell">{e.area}</TableCell>
                  <TableCell><Chip map={EXP_STATUS} value={e.status} testid={`exp-status-${e.id}`} /></TableCell>
                  <TableCell className="hidden sm:table-cell"><Chip map={RISK} value={e.risk} /></TableCell>
                  <TableCell className="hidden text-sm text-slate-600 lg:table-cell">{userName(map, e.owner_id)}</TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <div className="flex items-center gap-2">
                      <Progress value={e.progress} className="h-1.5" />
                      <span className="font-mono text-xs text-slate-500">{e.progress}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden text-sm text-slate-600 lg:table-cell">{fmtDate(e.target_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewExpedienteDialog open={newOpen} onClose={() => { setNewOpen(false); params.delete("nuevo"); setParams(params); }} onCreated={(e) => { refetch(); navigate(`/expedientes/${e.id}`); }} />
    </div>
  );
}
